<script>
import { mapGetters } from 'vuex';
import { shouldBeUrl } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { useConfig } from 'dashboard/composables/useConfig';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import SettingsSection from '../../../../components/SettingsSection.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import FacebookReauthorize from './facebook/Reauthorize.vue';
import InstagramReauthorize from './channels/instagram/Reauthorize.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import MicrosoftReauthorize from './channels/microsoft/Reauthorize.vue';
import GoogleReauthorize from './channels/google/Reauthorize.vue';
import AllegroReauthorize from './channels/allegro/Reauthorize.vue';
import WhatsappReauthorize from './channels/whatsapp/Reauthorize.vue';
import PreChatFormSettings from './PreChatForm/Settings.vue';
import WeeklyAvailability from './components/WeeklyAvailability.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import ConfigurationPage from './settingsPage/ConfigurationPage.vue';
import CollaboratorsPage from './settingsPage/CollaboratorsPage.vue';
import AutomaticPriorityPage from './settingsPage/AutomaticPriorityPage.vue';
import AutomaticLabelingPage from './settingsPage/AutomaticLabelingPage.vue';
import WidgetBuilder from './WidgetBuilder.vue';
import BotConfiguration from './components/BotConfiguration.vue';
import SlaPage from './settingsPage/SlaPage.vue';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import SenderNameExamplePreview from './components/SenderNameExamplePreview.vue';
import HtmlSignaturePreview from './components/HtmlSignaturePreview.vue';
import CountrySignatures from './components/CountrySignatures.vue';
import { countrySignatureFields } from './helpers/countrySignatures';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  respondFromOptions,
  respondFromValue,
  respondFromParams,
} from './helpers/respondFrom';
import { WIDGET_BUILDER_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

export default {
  components: {
    AutomaticPriorityPage,
    AutomaticLabelingPage,
    BotConfiguration,
    CollaboratorsPage,
    ConfigurationPage,
    FacebookReauthorize,
    GreetingsEditor,
    PreChatFormSettings,
    SettingIntroBanner,
    SettingsSection,
    WeeklyAvailability,
    WidgetBuilder,
    SenderNameExamplePreview,
    HtmlSignaturePreview,
    CountrySignatures,
    MicrosoftReauthorize,
    GoogleReauthorize,
    AllegroReauthorize,
    WhatsappReauthorize,
    NextButton,
    InstagramReauthorize,
    DuplicateInboxBanner,
    Banner,
    SlaPage,
    Editor,
  },
  mixins: [inboxMixin],
  setup() {
    const { enabledLanguages } = useConfig();
    return { v$: useVuelidate(), enabledLanguages };
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: '',
      greetingEnabled: true,
      greetingMessage: '',
      emailCollectEnabled: false,
      csatSurveyEnabled: false,
      csatSurveyOnEachMessage: false,
      explicitCsatLocale: null,
      senderNameType: 'friendly',
      businessName: '',
      locktoSingleConversation: false,
      allowMessagesAfterResolved: true,
      continuityViaEmail: true,
      respondFrom: '',
      forceNewConversationPerLoad: false,
      autoClosePendingInterval: null,
      selectedInboxName: '',
      selectedInboxInternalName: '',
      channelWebsiteUrl: '',
      webhookUrl: '',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      selectedFeatureFlags: [],
      replyTime: '',
      selectedTabIndex: 0,
      selectedPortalSlug: '',
      showBusinessNameInput: false,
      htmlSignature: '',
      countrySignatures: {},
      questionSuggestions: [],
      phoneDialingPrefix: '',
      resolveLaterText: '',
      autoSnoozeMinutes: null,
      autoResolveAfterFailedAttempts: null,
      widgetStatus: 'enabled',
      showWelcomeScreen: true,
      welcomeTaglineEditorMenuOptions: WIDGET_BUILDER_EDITOR_MENU_OPTIONS,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'inboxes/getUIFlags',
      portals: 'portals/allPortals',
      getActiveAgentBot: 'agentBots/getActiveAgentBot',
      inboxList: 'inboxes/getInboxes',
    }),
    respondFromOptions() {
      return respondFromOptions(
        this.inboxList,
        this.inbox.respond_from_channel_id
      );
    },
    allowedHtmlSignature() {
      return (
        this.inbox.channel_type === INBOX_TYPES.EMAIL ||
        this.inbox.channel_type === INBOX_TYPES.TWILIO ||
        this.allowedCountrySignatures
      );
    },
    // A marketplace inbox serves several countries and signs a reply in the
    // conversation's country, with html_signature as the fallback.
    allowedCountrySignatures() {
      return (
        this.inbox.channel_type === INBOX_TYPES.KAUFLAND ||
        this.inbox.channel_type === INBOX_TYPES.ALLEGRO
      );
    },
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    whatsAppAPIProviderName() {
      if (this.isAWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD');
      }
      return '';
    },
    tabs() {
      let visibleToAllChannelTabs = [
        {
          key: 'inbox_settings',
          name: this.$t('INBOX_MGMT.TABS.SETTINGS'),
        },
        {
          key: 'collaborators',
          name: this.$t('INBOX_MGMT.TABS.COLLABORATORS'),
        },
        {
          key: 'businesshours',
          name: this.$t('INBOX_MGMT.TABS.BUSINESS_HOURS'),
        },
      ];

      if (this.isAWebWidgetInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'preChatForm',
            name: this.$t('INBOX_MGMT.TABS.PRE_CHAT_FORM'),
          },
          {
            key: 'widgetBuilder',
            name: this.$t('INBOX_MGMT.TABS.WIDGET_BUILDER'),
          },
        ];
      }

      if (
        this.isATwilioChannel ||
        this.isASalesforceChannel ||
        this.isAnAllegroChannel ||
        this.isAWhatsAppCloudChannel ||
        this.isAKauflandChannel ||
        this.isALineChannel ||
        this.isAFacebookInbox ||
        this.isAPIInbox ||
        this.isPhoneInbox ||
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'configuration',
            name: this.$t('INBOX_MGMT.TABS.CONFIGURATION'),
          },
        ];
      }

      if (this.isAnEmailChannel) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'automaticPriority',
            name: this.$t('INBOX_MGMT.TABS.AUTOMATIC_PRIORITY'),
          },
        ];
      }

      if (this.isAnEmailChannel || this.isAWebWidgetInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'automaticLabeling',
            name: this.$t('INBOX_MGMT.TABS.AUTOMATIC_LABELING'),
          },
        ];
      }

      if (!this.isPhoneInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'sla',
            name: this.$t('INBOX_MGMT.TABS.SLA'),
          },
        ];
      }

      if (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.AGENT_BOTS
        ) &&
        !this.isPhoneInbox
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'botConfiguration',
            name: this.$t('INBOX_MGMT.TABS.BOT_CONFIGURATION'),
          },
        ];
      }
      return visibleToAllChannelTabs;
    },
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentInboxId);
    },

    inboxName() {
      if (this.isATwilioSMSChannel || this.isATwilioWhatsAppChannel) {
        return `${this.inbox.name} (${
          this.inbox.messaging_service_sid || this.inbox.phone_number
        })`;
      }
      if (this.isAWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.phone_number})`;
      }
      if (this.isAnEmailChannel) {
        return `${this.inbox.name} (${this.inbox.email})`;
      }
      return this.inbox.name;
    },
    canLocktoSingleConversation() {
      return (
        this.isASmsInbox ||
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAPIInbox
      );
    },
    inboxNameLabel() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL');
    },
    inboxNamePlaceHolder() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER');
    },
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
    instagramUnauthorized() {
      return this.isAInstagramChannel && this.inbox.reauthorization_required;
    },
    // Check if a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return this.inbox.channel_type === INBOX_TYPES.FB && instagramInbox;
    },
    microsoftUnauthorized() {
      return this.isAMicrosoftInbox && this.inbox.reauthorization_required;
    },
    facebookUnauthorized() {
      return this.isAFacebookInbox && this.inbox.reauthorization_required;
    },
    allegroUnauthorized() {
      return this.isAnAllegroChannel && this.inbox.reauthorization_required;
    },
    googleUnauthorized() {
      return this.isAGoogleInbox && this.inbox.reauthorization_required;
    },
    whatsappUnauthorized() {
      return (
        this.isAWhatsAppCloudChannel && this.inbox.reauthorization_required
      );
    },
    // An email inbox that signs in with a login and a password. There is nothing to reconnect:
    // the admin corrects the credentials on the configuration tab.
    imapLoginRejected() {
      return (
        this.isAnEmailChannel &&
        !this.isAMicrosoftInbox &&
        !this.isAGoogleInbox &&
        this.inbox.reauthorization_required
      );
    },
    activeAgentBot() {
      return this.getActiveAgentBot(this.currentInboxId);
    },
    hasConnectedBot() {
      return this.activeAgentBot && Object.keys(this.activeAgentBot).length > 0;
    },
    botAdminUrl() {
      return this.activeAgentBot?.bot_config?.admin_url;
    },
    // Stored as a web widget feature flag like the checkboxes below, but
    // presented as a dropdown alongside the other new-conversation settings.
    newConversationForgetsContact: {
      get() {
        return this.selectedFeatureFlags.includes(
          'new_conversation_forgets_contact'
        );
      },
      set(value) {
        const flags = this.selectedFeatureFlags.filter(
          flag => flag !== 'new_conversation_forgets_contact'
        );
        this.selectedFeatureFlags = value
          ? [...flags, 'new_conversation_forgets_contact']
          : flags;
      },
    },
  },
  watch: {
    $route(to) {
      if (to.name === 'settings_inbox_show') {
        this.fetchInboxSettings();
      }
    },
    csatSurveyEnabled(newValue) {
      if (!newValue) {
        this.csatSurveyOnEachMessage = false;
      }
    },
  },
  mounted() {
    this.fetchInboxSettings();
    this.fetchPortals();
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.currentInboxId);
  },
  methods: {
    fetchPortals() {
      this.$store.dispatch('portals/index');
    },
    handleFeatureFlag(e) {
      this.selectedFeatureFlags = this.toggleInput(
        this.selectedFeatureFlags,
        e.target.value
      );
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
    },
    fetchInboxSettings() {
      this.selectedTabIndex = 0;
      this.selectedAgents = [];
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      this.$store.dispatch('labels/get');
      this.$store.dispatch('inboxes/get').then(() => {
        this.avatarUrl = this.inbox.avatar_url;
        this.selectedInboxName = this.inbox.name;
        this.selectedInboxInternalName = this.inbox.internal_name;
        this.webhookUrl = this.inbox.webhook_url;
        this.greetingEnabled = this.inbox.greeting_enabled || false;
        this.greetingMessage = this.inbox.greeting_message || '';
        this.emailCollectEnabled = this.inbox.enable_email_collect;
        this.csatSurveyEnabled = this.inbox.csat_survey_enabled;
        this.csatSurveyOnEachMessage = this.inbox.csat_survey_on_each_message;
        this.explicitCsatLocale = this.inbox.explicit_csat_locale || null;
        this.senderNameType = this.inbox.sender_name_type;
        this.businessName = this.inbox.business_name;
        this.allowMessagesAfterResolved =
          this.inbox.allow_messages_after_resolved;
        this.continuityViaEmail = this.inbox.continuity_via_email;
        this.respondFrom = respondFromValue(
          this.inbox.respond_from_channel_id,
          this.inbox.respond_from_alias_id
        );
        this.forceNewConversationPerLoad =
          this.inbox.force_new_conversation_per_load || false;
        this.autoClosePendingInterval = this.inbox.auto_close_pending_interval;
        this.channelWebsiteUrl = this.inbox.website_url;
        this.channelWelcomeTitle = this.inbox.welcome_title;
        this.channelWelcomeTagline = this.inbox.welcome_tagline || '';
        this.selectedFeatureFlags = this.inbox.selected_feature_flags || [];
        this.replyTime = this.inbox.reply_time;
        this.locktoSingleConversation = this.inbox.lock_to_single_conversation;
        this.selectedPortalSlug = this.inbox.help_center
          ? this.inbox.help_center.slug
          : '';
        this.htmlSignature = this.inbox.html_signature;
        this.countrySignatures = { ...(this.inbox.country_signatures || {}) };
        this.questionSuggestions = this.inbox.question_suggestions || [];
        this.phoneDialingPrefix = this.inbox.phone_dialing_prefix;
        this.resolveLaterText = this.inbox.resolve_later_text || '';
        this.autoSnoozeMinutes = this.inbox.auto_snooze_minutes || null;
        this.autoResolveAfterFailedAttempts =
          this.inbox.auto_resolve_after_failed_attempts || null;
        this.widgetStatus = this.inbox.widget_status || 'enabled';
        this.showWelcomeScreen = this.inbox.show_welcome_screen !== false;
      });
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.currentInboxId,
          name: this.selectedInboxName,
          internal_name: this.selectedInboxInternalName,
          enable_email_collect: this.emailCollectEnabled,
          csat_survey_enabled: this.csatSurveyEnabled,
          csat_survey_on_each_message: this.csatSurveyOnEachMessage,
          explicit_csat_locale: this.explicitCsatLocale || null,
          allow_messages_after_resolved: this.allowMessagesAfterResolved,
          greeting_enabled: this.greetingEnabled,
          greeting_message: this.greetingMessage || '',
          portal_id: this.selectedPortalSlug
            ? this.portals.find(
                portal => portal.slug === this.selectedPortalSlug
              ).id
            : null,
          lock_to_single_conversation: this.locktoSingleConversation,
          sender_name_type: this.senderNameType,
          business_name: this.businessName || null,
          html_signature: this.htmlSignature || null,
          auto_close_pending_interval: this.autoClosePendingInterval || null,
          question_suggestions: JSON.stringify(this.questionSuggestions),
          phone_dialing_prefix: this.phoneDialingPrefix || null,
          ...respondFromParams(this.respondFrom),
          channel: {
            widget_color: this.inbox.widget_color,
            website_url: this.channelWebsiteUrl,
            webhook_url: this.webhookUrl,
            welcome_title: this.channelWelcomeTitle || '',
            welcome_tagline: this.channelWelcomeTagline || '',
            selectedFeatureFlags: this.selectedFeatureFlags,
            reply_time: this.replyTime || 'in_a_few_minutes',
            continuity_via_email: this.continuityViaEmail,
            force_new_conversation_per_load: this.forceNewConversationPerLoad,
            resolve_later_text: this.resolveLaterText.trim() || null,
            widget_status: this.widgetStatus,
            show_welcome_screen: this.showWelcomeScreen,
            auto_snooze_minutes: this.autoSnoozeMinutes || null,
            auto_resolve_after_failed_attempts:
              this.autoResolveAfterFailedAttempts || null,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        if (this.allowedCountrySignatures) {
          Object.assign(
            payload,
            countrySignatureFields(
              this.inbox.country_signatures,
              this.countrySignatures
            )
          );
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxAvatar',
          this.currentInboxId
        );
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(this.$t('INBOX_MGMT.DELETE.API.AVATAR_SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('INBOX_MGMT.DELETE.API.AVATAR_ERROR_MESSAGE')
        );
      }
    },
    toggleSenderNameType(key) {
      this.senderNameType = key;
    },
    updateHtmlSignature(htmlSignature) {
      this.htmlSignature = htmlSignature;
    },
    updateCountrySignatures(countrySignatures) {
      this.countrySignatures = countrySignatures;
    },
    onClickShowBusinessNameInput() {
      this.showBusinessNameInput = !this.showBusinessNameInput;
      if (this.showBusinessNameInput) {
        this.$nextTick(() => {
          this.$refs.businessNameInput.focus();
        });
      }
    },
    addQuestionSuggestion() {
      this.questionSuggestions.push({
        text: '',
        payload: '',
      });
    },
    removeQuestionSuggestion(index) {
      this.questionSuggestions.splice(index, 1);
    },
  },
  validations: {
    webhookUrl: {
      shouldBeUrl,
    },
    selectedInboxName: {},
    selectedInboxInternalName: {},
  },
};
</script>

<template>
  <div
    class="overflow-auto flex-grow flex-shrink pr-0 pl-0 w-full min-w-0 settings"
  >
    <SettingIntroBanner
      :header-image="inbox.avatarUrl"
      :header-title="inboxName"
    >
      <woot-tabs
        class="settings--tabs"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
    </SettingIntroBanner>
    <section class="mx-auto w-full max-w-6xl">
      <MicrosoftReauthorize v-if="microsoftUnauthorized" :inbox="inbox" />
      <FacebookReauthorize v-if="facebookUnauthorized" :inbox="inbox" />
      <GoogleReauthorize v-if="googleUnauthorized" :inbox="inbox" />
      <InstagramReauthorize v-if="instagramUnauthorized" :inbox="inbox" />
      <AllegroReauthorize v-if="allegroUnauthorized" :inbox="inbox" />
      <WhatsappReauthorize v-if="whatsappUnauthorized" :inbox="inbox" />
      <Banner v-if="imapLoginRejected" color="ruby" class="mx-8 mt-5">
        {{
          $t('INBOX_MGMT.RECONNECT.IMAP_LOGIN', {
            login: inbox.imap_login,
            tab: $t('INBOX_MGMT.TABS.CONFIGURATION'),
          })
        }}
      </Banner>
      <DuplicateInboxBanner
        v-if="hasDuplicateInstagramInbox"
        :content="$t('INBOX_MGMT.ADD.INSTAGRAM.DUPLICATE_INBOX_BANNER')"
        class="mx-8 mt-5"
      />
      <div v-if="selectedTabKey === 'inbox_settings'" class="mx-8">
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_UPDATE_TITLE')"
          :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_UPDATE_SUB_TEXT')"
          :show-border="false"
        >
          <woot-avatar-uploader
            :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_AVATAR.LABEL')"
            :src="avatarUrl"
            class="pb-4"
            delete-avatar
            @on-avatar-select="handleImageUpload"
            @on-avatar-delete="handleAvatarDelete"
          />
          <woot-input
            v-model="selectedInboxInternalName"
            class="pb-4"
            :class="{ error: v$.selectedInboxInternalName.$error }"
            :label="$t('INBOX_MGMT.ADD.INTERNAL_NAME.LABEL')"
            :placeholder="$t('INBOX_MGMT.ADD.INTERNAL_NAME.PLACEHOLDER')"
            :error="
              v$.selectedInboxInternalName.$error
                ? $t('INBOX_MGMT.ADD.INTERNAL_NAME.ERROR')
                : ''
            "
            @blur="v$.selectedInboxInternalName.$touch"
          />
          <woot-input
            v-model="selectedInboxName"
            class="pb-4"
            :class="{ error: v$.selectedInboxName.$error }"
            :label="inboxNameLabel"
            :placeholder="inboxNamePlaceHolder"
            :error="
              v$.selectedInboxName.$error
                ? $t('INBOX_MGMT.ADD.CHANNEL_NAME.ERROR')
                : ''
            "
            @blur="v$.selectedInboxName.$touch"
          />
          <woot-input
            v-if="isAPIInbox"
            v-model="webhookUrl"
            class="pb-4"
            :class="{ error: v$.webhookUrl.$error }"
            :label="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.PLACEHOLDER'
              )
            "
            :error="
              v$.webhookUrl.$error
                ? $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.ERROR')
                : ''
            "
            @blur="v$.webhookUrl.$touch"
          />
          <woot-input
            v-if="isAWebWidgetInbox"
            v-model="channelWebsiteUrl"
            class="pb-4"
            :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')"
            :placeholder="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER')
            "
          />
          <woot-input
            v-if="isAWebWidgetInbox"
            v-model="channelWelcomeTitle"
            class="pb-4"
            :label="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
              )
            "
          />

          <Editor
            v-if="isAWebWidgetInbox"
            v-model="channelWelcomeTagline"
            class="mb-4"
            :label="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
              )
            "
            :max-length="255"
            :enable-canned-responses="false"
            :enabled-menu-options="welcomeTaglineEditorMenuOptions"
          />

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL') }}
            <woot-color-picker v-model="inbox.widget_color" />
          </label>

          <label v-if="isAWhatsAppChannel" class="pb-4">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.LABEL') }}
            <input v-model="whatsAppAPIProviderName" type="text" disabled />
          </label>

          <label class="pb-4">
            {{
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL')
            }}
            <select v-model="greetingEnabled">
              <option :value="true">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.ENABLED'
                  )
                }}
              </option>
              <option :value="false">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.DISABLED'
                  )
                }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
                )
              }}
            </p>
          </label>
          <div v-if="greetingEnabled" class="pb-4">
            <GreetingsEditor
              v-model="greetingMessage"
              :label="
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                )
              "
              :richtext="!textAreaChannels"
            />
          </div>
          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.TITLE') }}
            <select v-model="replyTime">
              <option key="in_a_few_minutes" value="in_a_few_minutes">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_MINUTES'
                  )
                }}
              </option>
              <option key="in_a_few_hours" value="in_a_few_hours">
                {{
                  $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_HOURS')
                }}
              </option>
              <option key="in_a_day" value="in_a_day">
                {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_DAY') }}
              </option>
            </select>

            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.HELP_TEXT') }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX') }}
            <select v-model="emailCollectEnabled">
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.EMAIL_COLLECT_BOX.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.EMAIL_COLLECT_BOX.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX_SUB_TEXT'
                )
              }}
            </p>
          </label>

          <label class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CSAT') }}
            <select v-model="csatSurveyEnabled">
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CSAT.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CSAT.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CSAT_SUB_TEXT') }}
            </p>
          </label>

          <label class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EXPLICIT_CSAT_LOCALE') }}
            <select v-model="explicitCsatLocale">
              <option :value="null">
                {{
                  $t('INBOX_MGMT.SETTINGS_POPUP.EXPLICIT_CSAT_LOCALE_INHERIT')
                }}
              </option>
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.EXPLICIT_CSAT_LOCALE_SUB_TEXT')
              }}
            </p>
          </label>

          <label v-if="isAnEmailChannel" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CSAT_ON_EACH_MESSAGE') }}
            <select
              v-model="csatSurveyOnEachMessage"
              :disabled="!csatSurveyEnabled"
            >
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CSAT_ON_EACH_MESSAGE.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CSAT_ON_EACH_MESSAGE.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CSAT_ON_EACH_MESSAGE_SUB_TEXT'
                )
              }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED') }}
            <select v-model="allowMessagesAfterResolved">
              <option :value="true">
                {{
                  $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.ENABLED')
                }}
              </option>
              <option :value="false">
                {{
                  $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.DISABLED')
                }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED_SUB_TEXT'
                )
              }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL') }}
            <select v-model="continuityViaEmail">
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CONTINUITY_VIA_EMAIL.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CONTINUITY_VIA_EMAIL.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL_SUB_TEXT'
                )
              }}
            </p>
          </label>
          <label v-if="!isAnEmailChannel" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.RESPOND_FROM_EMAIL') }}
            <select v-model="respondFrom">
              <option value="">
                {{ $t('INBOX_MGMT.EDIT.RESPOND_FROM_EMAIL.NONE') }}
              </option>
              <option
                v-for="option in respondFromOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                isAWebWidgetInbox
                  ? $t(
                      'INBOX_MGMT.SETTINGS_POPUP.RESPOND_FROM_EMAIL_SUB_TEXT_WEBWIDGET'
                    )
                  : $t(
                      'INBOX_MGMT.SETTINGS_POPUP.RESPOND_FROM_EMAIL_SUB_TEXT_OTHER'
                    )
              }}
            </p>
          </label>
          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.FORCE_NEW_CONVERSATION_PER_LOAD_TITLE'
              )
            }}
            <select v-model="forceNewConversationPerLoad">
              <option :value="true">
                {{
                  $t('INBOX_MGMT.EDIT.FORCE_NEW_CONVERSATION_PER_LOAD.ENABLED')
                }}
              </option>
              <option :value="false">
                {{
                  $t('INBOX_MGMT.EDIT.FORCE_NEW_CONVERSATION_PER_LOAD.DISABLED')
                }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.FORCE_NEW_CONVERSATION_PER_LOAD_SUBTITLE'
                )
              }}
            </p>
          </label>
          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.NEW_CONVERSATION_FORGETS_CONTACT_TITLE'
              )
            }}
            <select v-model="newConversationForgetsContact">
              <option :value="true">
                {{
                  $t('INBOX_MGMT.EDIT.NEW_CONVERSATION_FORGETS_CONTACT.ENABLED')
                }}
              </option>
              <option :value="false">
                {{
                  $t(
                    'INBOX_MGMT.EDIT.NEW_CONVERSATION_FORGETS_CONTACT.DISABLED'
                  )
                }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.NEW_CONVERSATION_FORGETS_CONTACT_SUBTITLE'
                )
              }}
            </p>
          </label>
          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_CLOSE_PENDING') }}
            <input
              v-model="autoClosePendingInterval"
              type="number"
              min="1"
              step="1"
            />
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_CLOSE_PENDING_SUB_TEXT') }}
            </p>
          </label>
          <div class="pb-4">
            <label>
              {{ $t('INBOX_MGMT.HELP_CENTER.LABEL') }}
            </label>
            <select v-model="selectedPortalSlug" class="filter__question">
              <option value="">
                {{ $t('INBOX_MGMT.HELP_CENTER.PLACEHOLDER') }}
              </option>
              <option v-for="p in portals" :key="p.slug" :value="p.slug">
                {{ p.name }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{ $t('INBOX_MGMT.HELP_CENTER.SUB_TEXT') }}
            </p>
          </div>
          <woot-input
            v-model="phoneDialingPrefix"
            class="pb-4"
            :label="$t('INBOX_MGMT.EDIT.PHONE_DIALING_PREFIX.LABEL')"
            :placeholder="
              $t('INBOX_MGMT.EDIT.PHONE_DIALING_PREFIX.PLACEHOLDER')
            "
            :help-text="$t('INBOX_MGMT.EDIT.PHONE_DIALING_PREFIX.HELP_TEXT')"
          />
          <woot-input
            v-if="isPhoneInbox"
            v-model.number="autoSnoozeMinutes"
            type="number"
            min="0"
            class="pb-4"
            :label="$t('INBOX_MGMT.EDIT.AUTO_SNOOZE_MINUTES.LABEL')"
            :placeholder="$t('INBOX_MGMT.EDIT.AUTO_SNOOZE_MINUTES.PLACEHOLDER')"
            :help-text="$t('INBOX_MGMT.EDIT.AUTO_SNOOZE_MINUTES.HELP_TEXT')"
          />
          <woot-input
            v-if="isPhoneInbox"
            v-model.number="autoResolveAfterFailedAttempts"
            type="number"
            min="0"
            class="pb-4"
            :label="
              $t('INBOX_MGMT.EDIT.AUTO_RESOLVE_AFTER_FAILED_ATTEMPTS.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.EDIT.AUTO_RESOLVE_AFTER_FAILED_ATTEMPTS.PLACEHOLDER'
              )
            "
            :help-text="
              $t('INBOX_MGMT.EDIT.AUTO_RESOLVE_AFTER_FAILED_ATTEMPTS.HELP_TEXT')
            "
          />
          <label v-if="canLocktoSingleConversation" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION') }}
            <select v-model="locktoSingleConversation">
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.LOCK_TO_SINGLE_CONVERSATION.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.LOCK_TO_SINGLE_CONVERSATION.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION_SUB_TEXT'
                )
              }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox">
            {{ $t('INBOX_MGMT.FEATURES.LABEL') }}
          </label>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pt-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="attachments"
              @input="handleFeatureFlag"
            />
            <label for="attachments">
              {{ $t('INBOX_MGMT.FEATURES.DISPLAY_FILE_PICKER') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="emoji_picker"
              @input="handleFeatureFlag"
            />
            <label for="emoji_picker">
              {{ $t('INBOX_MGMT.FEATURES.DISPLAY_EMOJI_PICKER') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="end_conversation"
              @input="handleFeatureFlag"
            />
            <label for="end_conversation">
              {{ $t('INBOX_MGMT.FEATURES.ALLOW_END_CONVERSATION') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="show_start_new_conversation_button"
              @input="handleFeatureFlag"
            />
            <label for="show_start_new_conversation_button">
              {{ $t('INBOX_MGMT.FEATURES.SHOW_START_NEW_CONVERSATION_BUTTON') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="hide_input_under_start_new_conversation_button"
              @input="handleFeatureFlag"
            />
            <label for="hide_input_under_start_new_conversation_button">
              {{
                $t(
                  'INBOX_MGMT.FEATURES.HIDE_INPUT_UNDER_START_NEW_CONVERSATION_BUTTON'
                )
              }}
            </label>
          </div>
          <woot-input
            v-if="isAWebWidgetInbox"
            v-model="resolveLaterText"
            class="pb-4"
            :label="$t('INBOX_MGMT.EDIT.RESOLVE_LATER_TEXT.LABEL')"
            :placeholder="$t('INBOX_MGMT.EDIT.RESOLVE_LATER_TEXT.PLACEHOLDER')"
            :help-text="$t('INBOX_MGMT.EDIT.RESOLVE_LATER_TEXT.HELP_TEXT')"
          />
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="use_inbox_avatar_for_bot"
              @input="handleFeatureFlag"
            />
            <label for="use_inbox_avatar_for_bot">
              {{ $t('INBOX_MGMT.FEATURES.USE_INBOX_AVATAR_FOR_BOT') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="allow_offline_reply"
              @input="handleFeatureFlag"
            />
            <label for="allow_offline_reply">
              {{ $t('INBOX_MGMT.FEATURES.ALLOW_OFFLINE_REPLY') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="show_handoff_button"
              @input="handleFeatureFlag"
            />
            <label for="show_handoff_button">
              {{ $t('INBOX_MGMT.FEATURES.SHOW_HANDOFF_BUTTON') }}
            </label>
          </div>
          <div v-if="isPhoneInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="auto_close_old"
              @input="handleFeatureFlag"
            />
            <label for="auto_close_old">
              {{ $t('INBOX_MGMT.FEATURES.AUTO_CLOSE_OLD') }}
            </label>
          </div>
          <div v-if="isPhoneInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="auto_assign_missed_on_outgoing"
              @input="handleFeatureFlag"
            />
            <label for="auto_assign_missed_on_outgoing">
              {{ $t('INBOX_MGMT.FEATURES.AUTO_ASSIGN_MISSED_ON_OUTGOING') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 items-center pb-4">
            <input v-model="showWelcomeScreen" type="checkbox" />
            <label>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.SHOW_WELCOME_SCREEN') }}
            </label>
            <span
              v-tooltip.top="
                $t('INBOX_MGMT.SETTINGS_POPUP.SHOW_WELCOME_SCREEN_TOOLTIP')
              "
              class="i-lucide-info size-4 text-n-slate-10"
            />
          </div>
          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WIDGET_STATUS_LABEL') }}
            <select v-model="widgetStatus">
              <option value="enabled">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WIDGET_STATUS_ENABLED') }}
              </option>
              <option value="business_hours">
                {{
                  $t('INBOX_MGMT.SETTINGS_POPUP.WIDGET_STATUS_BUSINESS_HOURS')
                }}
              </option>
              <option value="disabled">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WIDGET_STATUS_DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WIDGET_STATUS_HELP_TEXT') }}
            </p>
          </label>
        </SettingsSection>
        <SettingsSection
          v-if="isAWebWidgetInbox"
          :title="$t('INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.TITLE')"
          :sub-title="$t('INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.SUB_TEXT')"
          :show-border="false"
        >
          <div class="pb-4">
            <div class="template-messages-form">
              <div class="mb-4">
                <div class="flex justify-between items-center mb-3">
                  <label class="block text-sm font-medium">
                    {{
                      $t('INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.QUESTIONS_LABEL')
                    }}
                  </label>
                  <NextButton
                    ghost
                    blue
                    size="small"
                    :label="
                      $t('INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.ADD_QUESTION')
                    "
                    @click="addQuestionSuggestion"
                  />
                </div>

                <div
                  v-for="(message, index) in questionSuggestions"
                  :key="index"
                  class="rounded-md p-3 pb-0"
                >
                  <div class="flex justify-between items-start mb-2">
                    <span />
                    <button
                      type="button"
                      class="text-red-600 hover:text-red-800 text-sm"
                      @click="removeQuestionSuggestion(index)"
                    >
                      {{ $t('INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.REMOVE') }}
                    </button>
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">
                        {{
                          $t(
                            'INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.QUESTION_TEXT'
                          )
                        }}
                      </label>
                      <input
                        v-model="message.text"
                        type="text"
                        :placeholder="
                          $t(
                            'INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.QUESTION_TEXT_PLACEHOLDER'
                          )
                        "
                        class="mb-0 w-full text-sm"
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">
                        {{
                          $t(
                            'INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.QUESTION_PAYLOAD'
                          )
                        }}
                      </label>
                      <input
                        v-model="message.payload"
                        type="text"
                        :placeholder="
                          $t(
                            'INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.QUESTION_PAYLOAD_PLACEHOLDER'
                          )
                        "
                        class="mb-0 w-full text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex gap-2 pb-4">
              <div v-if="!hasConnectedBot">
                <p class="text-sm text-gray-600">
                  {{
                    $t('INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.NO_BOT_MESSAGE')
                  }}
                </p>
              </div>
              <div v-else>
                <p class="text-sm text-gray-600">
                  {{
                    $t(
                      'INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.BOT_CONNECTED_MESSAGE'
                    )
                  }}
                  <a
                    v-if="botAdminUrl"
                    :href="botAdminUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:text-blue-800 underline"
                  >
                    {{
                      $t(
                        'INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.BOT_SETTINGS_LINK'
                      )
                    }}
                  </a>
                  <span v-else>
                    {{
                      $t(
                        'INBOX_MGMT.EDIT.QUESTION_SUGGESTIONS.BOT_SETTINGS_LINK'
                      )
                    }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </SettingsSection>
        <SettingsSection
          v-if="isAWebWidgetInbox || isAnEmailChannel"
          :title="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.TITLE')"
          :sub-title="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.SUB_TEXT')"
          :show-border="false"
        >
          <div class="pb-4">
            <SenderNameExamplePreview
              :sender-name-type="senderNameType"
              :business-name="businessName"
              @update="toggleSenderNameType"
            />
            <div class="flex flex-col gap-2 items-start mt-2">
              <NextButton
                ghost
                blue
                :label="
                  $t(
                    'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.BUTTON_TEXT'
                  )
                "
                @click="onClickShowBusinessNameInput"
              />
              <div v-if="showBusinessNameInput" class="flex gap-2 w-[80%]">
                <input
                  ref="businessNameInput"
                  v-model="businessName"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.PLACEHOLDER'
                    )
                  "
                  class="mb-0"
                  type="text"
                />
                <NextButton
                  :label="
                    $t(
                      'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.SAVE_BUTTON_TEXT'
                    )
                  "
                  class="flex-shrink-0"
                  @click="updateInbox"
                />
              </div>
            </div>
          </div>
        </SettingsSection>
        <div
          v-if="allowedHtmlSignature"
          class="ml-0 mr-0 flex py-8 w-full xl:w-3/4 flex-col xl:flex-row"
        >
          <div class="w-full xl:w-1/4 min-w-0 xl:max-w-[30%] pr-12">
            <p
              class="text-base text-woot-500 dark:text-woot-500 mb-0 font-medium"
            >
              {{ $t('INBOX_MGMT.EDIT.SIGNATURE.TITLE') }}
            </p>
            <p
              class="text-sm mb-2 text-slate-700 dark:text-slate-300 leading-5 tracking-normal mt-2"
            >
              {{ $t('INBOX_MGMT.EDIT.SIGNATURE.SUB_TEXT') }}
            </p>
          </div>
          <div class="w-full">
            <HtmlSignaturePreview
              :html-signature="htmlSignature"
              @update="updateHtmlSignature"
            />
          </div>
        </div>
        <div
          v-if="allowedCountrySignatures"
          class="ml-0 mr-0 flex py-8 w-full xl:w-3/4 flex-col xl:flex-row"
        >
          <div class="w-full xl:w-1/4 min-w-0 xl:max-w-[30%] pr-12">
            <p
              class="text-base text-woot-500 dark:text-woot-500 mb-0 font-medium"
            >
              {{ $t('INBOX_MGMT.EDIT.COUNTRY_SIGNATURES.TITLE') }}
            </p>
            <p
              class="text-sm mb-2 text-slate-700 dark:text-slate-300 leading-5 tracking-normal mt-2"
            >
              {{ $t('INBOX_MGMT.EDIT.COUNTRY_SIGNATURES.SUB_TEXT') }}
            </p>
          </div>
          <div class="w-full">
            <CountrySignatures
              :key="inbox.id"
              :signatures="countrySignatures"
              @update="updateCountrySignatures"
            />
          </div>
        </div>
        <SettingsSection :show-border="false">
          <NextButton
            v-if="isAPIInbox"
            type="submit"
            :disabled="v$.webhookUrl.$invalid"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="uiFlags.isUpdating"
            @click="updateInbox"
          />
          <NextButton
            v-else
            type="submit"
            :disabled="v$.$invalid"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="uiFlags.isUpdating"
            @click="updateInbox"
          />
        </SettingsSection>
      </div>

      <div v-if="selectedTabKey === 'collaborators'" class="mx-8">
        <CollaboratorsPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'automaticPriority'" class="mx-8">
        <AutomaticPriorityPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'automaticLabeling'" class="mx-8">
        <AutomaticLabelingPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'configuration'">
        <ConfigurationPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'preChatForm'">
        <PreChatFormSettings :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'businesshours'">
        <WeeklyAvailability :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'widgetBuilder'">
        <WidgetBuilder :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'sla'" class="mx-8">
        <SlaPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'botConfiguration'">
        <BotConfiguration :inbox="inbox" />
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.settings--tabs {
  ::v-deep .tabs {
    @apply p-0;
  }
}
</style>
